import React, { useCallback, useEffect, useRef, useState } from "react";
import Navbar from "../../Navbar";
import { UrlApi } from "../../../services/apirest";
import { logEventos } from "../../../services/services";
import axios from "axios";
import {
  Box,
  Container,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  Tooltip,
  IconButton,
  Chip,
  CircularProgress,
  Snackbar,
  Alert,
  InputAdornment,
} from "@mui/material";
import {
  AccountBalance,
  LocationOn,
  Save,
  Cancel,
  AttachMoney,
  InfoOutlined,
  LockOutlined,
  PercentOutlined,
} from "@mui/icons-material";

// ─── helpers ─────────────────────────────────────────────────────────────────

const isDecimalValid = (v) => v === "" || /^\d*\.?\d*$/.test(v);

// ─── Componentes estáticos (fuera del padre para no recrearlos) ───────────────

const HeadCell = ({ children, tooltip, align = "center", sx = {} }) => (
  <TableCell align={align} sx={{ fontWeight: 700, fontSize: 12, py: 1.2, ...sx }}>
    <Tooltip title={tooltip || ""} placement="top">
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: align === "center" ? "center" : "flex-start", gap: 0.4 }}>
        {children}
        {tooltip && <InfoOutlined sx={{ fontSize: 13, color: "text.secondary" }} />}
      </Box>
    </Tooltip>
  </TableCell>
);

const PadronChip = ({ padron }) =>
  Number(padron) === 1 ? (
    <Chip label="Padrón" size="small" color="primary" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
  ) : (
    <Chip label="Manual" size="small" color="default" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
  );


/** Campo editable: recibe solo primitivos → React.memo lo evita re-renderizar si nada cambia */
const EditField = React.memo(({
  disabled, reason, value, originalValue,
  hasError, errorMsg, isRowChanged, adornment, placeholder, onChange,
}) => {
  const input = (
    <TextField
      size="small"
      variant="outlined"
      disabled={disabled}
      value={value}
      placeholder={disabled ? "—" : placeholder}
      error={hasError}
      helperText={hasError ? errorMsg : undefined}
      onChange={(e) => onChange(e.target.value)}
      inputProps={{ style: { textAlign: "right", fontSize: 13 } }}
      InputProps={
        adornment
          ? {
              startAdornment: (
                <InputAdornment position="start">
                  <Typography variant="caption" color={disabled ? "text.disabled" : "text.secondary"}>
                    {adornment}
                  </Typography>
                </InputAdornment>
              ),
            }
          : undefined
      }
      sx={{
        width: 90,
        "& .MuiOutlinedInput-root": {
          bgcolor: disabled
            ? "action.disabledBackground"
            : isRowChanged && String(value) !== String(originalValue ?? "")
            ? "#fff8e1"
            : "white",
          fontSize: 13,
        },
      }}
    />
  );

  if (disabled && reason) {
    return (
      <Tooltip title={reason} placement="top" arrow>
        <span>{input}</span>
      </Tooltip>
    );
  }
  return input;
});

/** Fila memoizada: solo re-renderiza cuando cambian SUS datos (editedData / errorsData / isSaving) */
const ImpuestoRow = React.memo(({
  row, idx, editedData, errorsData, isSaving, onChange, onSave, onCancel,
}) => {
  const changed = !!editedData && Object.keys(editedData).length > 0;
  const hasErr  = !!errorsData && Object.keys(errorsData).length > 0;

  const getVal = (field) => {
    if (editedData && editedData[field] !== undefined) return editedData[field];
    return row[field] ?? "";
  };

  const getFieldState = (field) => {
    const isPadron   = Number(row.padron) === 1;
    const notPercFija = Number(row.percepcion_fija) !== 1;

    if ((isPadron || notPercFija) && field === "alicuota") {
      return {
        disabled: true,
        reason: isPadron
          ? "Las percepciones de padrón no requieren alícuota propia"
          : "Percepción fija: no requiere alícuota propia",
      };
    }

    const netoVal    = String(getVal("neto_minimo")).trim();
    const percMinVal = String(getVal("percepcion_minima")).trim();

    if (field === "percepcion_minima" && netoVal !== "" && netoVal !== "0") {
      return { disabled: true, reason: "Si hay neto mínimo, no se puede registrar percepción mínima" };
    }
    if (field === "neto_minimo" && percMinVal !== "" && percMinVal !== "0") {
      return { disabled: true, reason: "Si hay percepción mínima, no se puede registrar neto mínimo" };
    }

    return { disabled: false, reason: "" };
  };

  const errRow = errorsData || {};

  const fields = [
    { key: "alicuota",             adornment: "%" },
    { key: "alicuota_maxima_iibb", adornment: "%" },
    { key: "neto_minimo",          adornment: "$" },
    { key: "percepcion_minima",    adornment: "$" },
  ];

  return (
    <TableRow
      sx={{
        bgcolor: changed ? "#fff8e1" : idx % 2 === 0 ? "white" : "#f7f9fc",
        "&:hover": { bgcolor: changed ? "#fff3cd" : "#eef4ff" },
        transition: "background-color 0.15s",
        borderLeft: changed ? "3px solid #f9a825" : "3px solid transparent",
      }}
    >
      {/* Provincia */}
      <TableCell sx={{ pl: 1.5, py: 0.5 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <LocationOn sx={{ fontSize: 14, color: "primary.main" }} />
          <Typography variant="body2" fontWeight={600} fontSize={13}>
            {row.descripcion}
          </Typography>
        </Box>
      </TableCell>

      <TableCell align="center" sx={{ py: 0.5 }}>
        <PadronChip padron={row.padron} />
      </TableCell>

      {/* Separador */}
      <TableCell sx={{ p: 0, bgcolor: "#e0e0e0", width: 1 }} />

      {/* Campos editables */}
      {fields.map(({ key, adornment }) => {
        const { disabled, reason } = getFieldState(key);
        return (
          <TableCell key={key} align="center" sx={{ py: 0.5, px: 0.8 }}>
            <EditField
              disabled={disabled}
              reason={reason}
              value={getVal(key)}
              originalValue={row[key] ?? ""}
              hasError={!!errRow[key]}
              errorMsg={errRow[key]}
              isRowChanged={changed}
              adornment={adornment}
              placeholder="0.00"
              onChange={(v) => onChange(row.idimpuestos, key, v)}
            />
          </TableCell>
        );
      })}

      {/* Acciones */}
      <TableCell align="center" sx={{ py: 0.5 }}>
        <Box sx={{ display: "flex", justifyContent: "center", gap: 0.5 }}>
          {isSaving ? (
            <CircularProgress size={20} />
          ) : (
            <>
              <Tooltip
                title={hasErr ? "Corregí los errores antes de guardar" : changed ? "Guardar cambios" : "Sin cambios"}
                placement="top"
              >
                <span>
                  <IconButton
                    size="small"
                    color="success"
                    disabled={!changed || hasErr}
                    onClick={() => onSave(row)}
                    sx={{ bgcolor: changed && !hasErr ? "#e8f5e9" : "transparent", "&:hover": { bgcolor: "#c8e6c9" } }}
                  >
                    <Save fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {changed && (
                <Tooltip title="Descartar cambios" placement="top">
                  <IconButton
                    size="small"
                    color="warning"
                    onClick={() => onCancel(row.idimpuestos)}
                    sx={{ bgcolor: "#fff3e0", "&:hover": { bgcolor: "#ffe0b2" } }}
                  >
                    <Cancel fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
});

// ─── Componente principal ─────────────────────────────────────────────────────

function ConfiguracionesIIBB() {
  const [impuestos, setImpuestos] = useState(null);
  const [loading, setLoading]     = useState(true);
  const [editedRows, setEditedRows] = useState({});
  const [errors, setErrors]         = useState({});
  const [saving, setSaving]         = useState({});
  const [notify, setNotify] = useState({ open: false, severity: "success", mensaje: "", color: "#D4EFDF" });

  // Refs para que los callbacks estables lean siempre el valor actual
  const editedRowsRef = useRef(editedRows);
  const errorsRef     = useRef(errors);
  useEffect(() => { editedRowsRef.current = editedRows; }, [editedRows]);
  useEffect(() => { errorsRef.current = errors; }, [errors]);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  const fetchImpuestos = useCallback(() => {
    setLoading(true);
    fetch(UrlApi + "/impuestos/config-iibb")
      .then((r) => r.json())
      .then((data) => {
        if (!Array.isArray(data)) console.warn("config-iibb no devolvió un array:", data);
        setImpuestos(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => { console.error("Error fetching config-iibb:", err); setLoading(false); });
  }, []);

  useEffect(() => {
    fetchImpuestos();
    logEventos("Acceso a Configuración IIBB", "Ventas", "Configuraciones IIBB", localStorage.getItem("user"));
  }, [fetchImpuestos]);

  // ── handleChange estable: usa functional setState, sin dependencias ─────────
  const handleChange = useCallback((idimpuestos, field, value) => {
    if ((field === "alicuota" || field === "alicuota_maxima_iibb") && !isDecimalValid(value)) {
      setErrors((prev) => ({
        ...prev,
        [idimpuestos]: { ...(prev[idimpuestos] || {}), [field]: "Solo se admiten valores decimales (ej: 3.5)" },
      }));
      return;
    }

    setErrors((prev) => {
      const rowErrors = { ...(prev[idimpuestos] || {}) };
      delete rowErrors[field];
      if (Object.keys(rowErrors).length === 0) {
        const next = { ...prev };
        delete next[idimpuestos];
        return next;
      }
      return { ...prev, [idimpuestos]: rowErrors };
    });

    setEditedRows((prev) => ({
      ...prev,
      [idimpuestos]: { ...(prev[idimpuestos] || {}), [field]: value },
    }));
  }, []);

  // ── handleSave estable: lee estado actual via refs ─────────────────────────
  const handleSave = useCallback(async (row) => {
    const currentErrors = errorsRef.current[row.idimpuestos];
    if (currentErrors && Object.keys(currentErrors).length > 0) return;
    const changes = editedRowsRef.current[row.idimpuestos];
    if (!changes || Object.keys(changes).length === 0) return;

    setSaving((prev) => ({ ...prev, [row.idimpuestos]: true }));
    try {
      await axios.put(`${UrlApi}/impuestos/${row.idimpuestos}`, changes);
      setNotify({ open: true, mensaje: `${row.descripcion} guardado correctamente.`, severity: "success", color: "#D4EFDF" });
      setEditedRows((prev) => { const next = { ...prev }; delete next[row.idimpuestos]; return next; });
      fetchImpuestos();
    } catch {
      setNotify({ open: true, mensaje: "Error al guardar. Intente nuevamente.", severity: "error", color: "#FADBD8" });
    } finally {
      setSaving((prev) => ({ ...prev, [row.idimpuestos]: false }));
    }
  }, [fetchImpuestos]);

  // ── handleCancel estable ───────────────────────────────────────────────────
  const handleCancel = useCallback((idimpuestos) => {
    setEditedRows((prev) => { const next = { ...prev }; delete next[idimpuestos]; return next; });
    setErrors((prev)     => { const next = { ...prev }; delete next[idimpuestos]; return next; });
  }, []);

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div>
      <Navbar />

      <Snackbar
        open={notify.open}
        autoHideDuration={2500}
        onClose={() => setNotify((p) => ({ ...p, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setNotify((p) => ({ ...p, open: false }))}
          severity={notify.severity}
          variant="filled"
          sx={{ bgcolor: notify.color, color: "black" }}
        >
          {notify.mensaje}
        </Alert>
      </Snackbar>

      <Container maxWidth="xl" sx={{ py: 1.5 }}>

        {/* Header */}
        <Paper elevation={0} sx={{ mb: 1.5, p: 1.5, background: "linear-gradient(135deg, #1976d2 0%, #1565c0 100%)", borderRadius: 2, color: "white" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.2 }}>
            <AccountBalance sx={{ fontSize: 28 }} />
            <Box>
              <Typography variant="h6" fontWeight={700} letterSpacing={0.3}>
                Configuración de Impuestos IIBB
              </Typography>
              <Typography variant="caption" sx={{ opacity: 0.85 }}>
                Ingresos Brutos por Provincia — Alícuotas y mínimos aplicables
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* Leyenda */}
{/*         <Paper elevation={0} sx={{ mb: 1.5, px: 1.5, py: 0.8, bgcolor: "#f5f9ff", border: "1px solid #bbdefb", borderRadius: 2 }}>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center" }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Chip label="Padrón" size="small" color="primary" variant="outlined" sx={{ fontSize: 10, height: 20 }} />
              <Typography variant="caption" color="text.secondary">Solo: alíc. máx., neto mín. o perc. mín.</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Chip label="Fija" size="small" sx={{ fontSize: 10, height: 20, bgcolor: "#e8f5e9", color: "#2e7d32" }} />
              <Typography variant="caption" color="text.secondary">Igual que padrón</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Chip label="Variable" size="small" sx={{ fontSize: 10, height: 20, bgcolor: "#e3f2fd", color: "#1565c0" }} />
              <Typography variant="caption" color="text.secondary">Admite todos los campos</Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <LockOutlined sx={{ fontSize: 13, color: "warning.main" }} />
              <Typography variant="caption" color="text.secondary">Neto mín. y perc. mín. son excluyentes</Typography>
            </Box>
          </Box>
        </Paper> */}

        {/* Tabla */}
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: 260, gap: 2 }}>
            <CircularProgress />
            <Typography color="text.secondary">Cargando impuestos...</Typography>
          </Box>
        ) : !impuestos || impuestos.length === 0 ? (
          <Paper sx={{ p: 5, textAlign: "center" }}>
            <Typography color="text.secondary">No se encontraron impuestos registrados.</Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper} elevation={2} sx={{ borderRadius: 2, overflowX: "auto" }}>
            <Table size="small" stickyHeader sx={{ tableLayout: "fixed", minWidth: 700 }}>
              <TableHead>
                <TableRow sx={{ "& th": { bgcolor: "#1565c0", color: "white" } }}>
                  <HeadCell align="left" sx={{ width: 160, pl: 1.5 }}>
                    <LocationOn sx={{ fontSize: 14, mr: 0.3 }} />
                    Provincia
                  </HeadCell>
                  <HeadCell tooltip="Indica si la percepción viene del padrón o se configura manualmente" sx={{ width: 75 }}>
                    Origen
                  </HeadCell>
                  <TableCell sx={{ bgcolor: "#0d47a1", width: 4, p: 0 }} />
                  <HeadCell tooltip="Alícuota de percepción (deshabilitada para padrón y percepción fija)" sx={{ width: 100, bgcolor: "#0d47a1 !important" }}>
                    <PercentOutlined sx={{ fontSize: 13 }} />Alícuota
                  </HeadCell>
                  <HeadCell tooltip="Alícuota máxima de IIBB aplicable (valor decimal, ej: 3.5)" sx={{ width: 100, bgcolor: "#0d47a1 !important" }}>
                    <PercentOutlined sx={{ fontSize: 13 }} />Alíc. Máx.
                  </HeadCell>
                  <HeadCell tooltip="Monto neto mínimo imponible. Excluyente con percepción mínima." sx={{ width: 100, bgcolor: "#0d47a1 !important" }}>
                    <AttachMoney sx={{ fontSize: 13 }} />Neto Mín.
                  </HeadCell>
                  <HeadCell tooltip="Monto mínimo de percepción. Excluyente con neto mínimo." sx={{ width: 100, bgcolor: "#0d47a1 !important" }}>
                    <AttachMoney sx={{ fontSize: 13 }} />Perc. Mín.
                  </HeadCell>
                  <TableCell align="center" sx={{ bgcolor: "#1565c0", color: "white", fontWeight: 700, fontSize: 12, width: 76 }}>
                    Acciones
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {impuestos.map((row, idx) => (
                  <ImpuestoRow
                    key={row.idimpuestos}
                    row={row}
                    idx={idx}
                    editedData={editedRows[row.idimpuestos] ?? null}
                    errorsData={errors[row.idimpuestos] ?? null}
                    isSaving={!!saving[row.idimpuestos]}
                    onChange={handleChange}
                    onSave={handleSave}
                    onCancel={handleCancel}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {impuestos && impuestos.length > 0 && (
          <Box sx={{ mt: 1, display: "flex", justifyContent: "flex-end" }}>
            <Typography variant="caption" color="text.secondary">
              {impuestos.length} provincias cargadas — Los cambios se guardan individualmente por fila
            </Typography>
          </Box>
        )}
      </Container>
    </div>
  );
}

export default ConfiguracionesIIBB;
